import { Text } from "@/components/ui/baseUi/text"
import { BoxShopCardItem } from "@/modules/boxShop/components/boxShopCardItem"
import type { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import type { BoxShopItemCategory } from "@/modules/boxShop/gateway/boxShopApiDto"
import { useBoxShopItemsInfinite } from "@/modules/boxShop/hooks/useBoxShopItem"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import BoxShopEmpty from "@images/empty/box_shop_empty.svg"
import { useCallback, useEffect, useRef } from "react"

type BoxShopShoppingListItemProps = {
  onAddItem?: (item: BoxShopItemEntity) => void
  className?: string
  category?: BoxShopItemCategory
  searchTerm?: string
} & React.ComponentProps<"div">

export function BoxShopShoppingListItem({
  onAddItem,
  className,
  category,
  searchTerm,
  ...props
}: BoxShopShoppingListItemProps) {
  const {
    data: items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBoxShopItemsInfinite({
    category,
    searchTerm,
  })

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (items.length === 0) {
    return (
      <div className="flex h-40 w-full flex-row gap-4 rounded-xl bg-neutral-100 px-6">
        <img src={BoxShopEmpty} alt="Search Empty" className="size-40" />
        <div className="flex flex-col justify-center">
          <Text fontStyle="TL">
            {searchTerm && searchTerm !== ""
              ? word("explore.search.no_result.title", { searchTerms: searchTerm })
              : word("global.no_result")}
          </Text>
          <Text fontStyle="BS">{word("explore.search.no_result.message")}</Text>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col gap-3", className)} {...props}>
      <div className="flex flex-wrap gap-3">
        {items.map((item) => (
          <BoxShopCardItem key={item.id} item={item} onAddItem={onAddItem} />
        ))}
      </div>

      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
          <Text fontStyle="BM">{word("global.loading")}</Text>
        </div>
      )}
    </div>
  )
}
